import React from "react";
import styled, { keyframes } from "styled-components";
import { useCampaignsQuery } from "hooks/usePromotions";
import { API } from 'utils/Helper';

// Glassmorphic Premium Campaign Container
const CampaignGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  margin: 16px 0;
`;

const CampaignCard = styled.div`
  background: rgba(30, 41, 59, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(168, 85, 247, 0.4);
    box-shadow: 0 10px 25px -5px rgba(168, 85, 247, 0.15),
                0 8px 10px -6px rgba(168, 85, 247, 0.15);
  }
`;

const BannerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  background: #0f172a;
  overflow: hidden;
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${CampaignCard}:hover & {
    transform: scale(1.05);
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  bottom: -16px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.8);
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
`;

const IconImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const CardContent = styled.div`
  padding: 20px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #f8fafc;
  margin: 0;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  font-size: 11px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 30px;
`;

const CardFooter = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 12px;
`;

const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #a855f7;
  letter-spacing: 0.05em;
`;

const EndTimeText = styled.span`
  font-size: 10px;
  color: #64748b;
`;

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const SkeletonCardWrapper = styled.div`
  .shimmer-banner {
    width: 100%;
    height: 140px;
    background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
    background-size: 600px 100%;
    animation: ${shimmer} 1.4s infinite linear;
    border-radius: 10px 10px 0 0;
  }

  .shimmer-footer {
    min-height: 55px;
    background: #292d2e;
    border: 1px solid #3a4142;
    border-top: none;
    border-radius: 0 0 10px 10px;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .shimmer-title {
    width: 120px;
    height: 14px;
    background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
    background-size: 600px 100%;
    animation: ${shimmer} 1.4s infinite linear;
    border-radius: 4px;
  }

  .shimmer-progress {
    width: 70px;
    height: 14px;
    background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
    background-size: 600px 100%;
    animation: ${shimmer} 1.4s infinite linear;
    border-radius: 4px;
  }
`;

export default function CampaignsList({ showOn }) {
  const { data: campaigns = [], isLoading: loading } = useCampaignsQuery(showOn);

  if (loading) {
    if (showOn === "promotions") {
      return (
        <div className="main-card-promot" style={{ marginTop: "20px" }}>
          {[1, 2, 3].map((i) => (
            <SkeletonCardWrapper key={i} className="CardPromotion-main-in">
              <div className="shimmer-banner" />
              <div className="shimmer-footer">
                <div className="shimmer-title" />
                <div className="shimmer-progress" />
              </div>
            </SkeletonCardWrapper>
          ))}
        </div>
      );
    }

    return (
      <div className="my-6">
        <CampaignGrid>
          {[1, 2, 3].map((i) => (
            <SkeletonCardWrapper key={i}>
              <div className="shimmer-banner" style={{ height: "120px" }} />
              <div className="shimmer-footer">
                <div className="shimmer-title" />
                <div className="shimmer-progress" />
              </div>
            </SkeletonCardWrapper>
          ))}
        </CampaignGrid>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return null; // Don't render anything if no active campaigns for this slot
  }

  if (showOn === "promotions") {
    return (
      <div className="main-card-promot" style={{ marginTop: "20px" }}>
        {campaigns.map((camp) => {
          const display = camp.display_json || {};
          const rawBanner = display.banner;
          const bannerUrl = rawBanner
            ? (rawBanner.startsWith("http") || rawBanner.startsWith("/uploads")
                ? (rawBanner.startsWith("http") ? rawBanner : `${API}${rawBanner}`)
                : rawBanner)
            : "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600";

          const rawIcon = display.icon;
          const iconUrl = rawIcon
            ? (rawIcon.startsWith("http") || rawIcon.startsWith("/uploads")
                ? (rawIcon.startsWith("http") ? rawIcon : `${API}${rawIcon}`)
                : rawIcon)
            : "/assets/bcimages/promotion/starimg.webp";

          return (
            <div 
              className="CardPromotion-main-in" 
              key={camp.id}
              style={{ 
                cursor: "pointer", 
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)" 
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div className="bc-promotion-main">
                <img 
                  src={bannerUrl} 
                  className="bcpromotionimg" 
                  alt={display.title || camp.name} 
                  style={{ 
                    borderRadius: "10px 10px 0 0",
                    display: "block",
                    objectFit: "cover",
                    height: "140px"
                  }} 
                />
                <img src={iconUrl} className="rank-img" alt="Exclusive badge" />
              </div>
              <div className="casino-button-in" style={{ minHeight: "55px", alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: "left", overflow: "hidden" }}>
                  <span className="deposite-heading" style={{ color: "#fff", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                    {display.title || camp.name}
                  </span>
                  {camp.end_date && (
                    <span className="casino-button-in-time" style={{ fontSize: "11px" }}>
                      Ends {new Date(camp.end_date).toLocaleString()}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span className="casino-progress" style={{ fontSize: "12px", fontWeight: "bold" }}>In Progress</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="my-6">
      <CampaignGrid>
        {campaigns.map((camp) => {
          const display = camp.display_json || {};
          const rawBanner = display.banner;
          const bannerUrl = rawBanner
            ? (rawBanner.startsWith("http") || rawBanner.startsWith("/uploads")
                ? (rawBanner.startsWith("http") ? rawBanner : `${API}${rawBanner}`)
                : rawBanner)
            : "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600";

          const rawIcon = display.icon;
          const iconUrl = rawIcon
            ? (rawIcon.startsWith("http") || rawIcon.startsWith("/uploads")
                ? (rawIcon.startsWith("http") ? rawIcon : `${API}${rawIcon}`)
                : rawIcon)
            : "/assets/bcimages/promotion/starimg.webp";

          return (
            <CampaignCard key={camp.id}>
              <BannerWrapper>
                <BannerImg src={bannerUrl} alt={display.title} />
                <IconWrapper>
                  <IconImg src={iconUrl} alt="Campaign Icon" />
                </IconWrapper>
              </BannerWrapper>
              <CardContent>
                <CardTitle>{display.title || camp.name}</CardTitle>
                <CardDesc>{display.description || "Grab your bonus now!"}</CardDesc>
                <CardFooter>
                  <StatusBadge>Active</StatusBadge>
                  {camp.end_date && (
                    <EndTimeText>
                      Ends {new Date(camp.end_date).toLocaleDateString()}
                    </EndTimeText>
                  )}
                </CardFooter>
              </CardContent>
            </CampaignCard>
          );
        })}
      </CampaignGrid>
    </div>
  );
}
